import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  useTheme,
  alpha,
  Skeleton,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  RocketLaunchOutlined,
  ScheduleOutlined,
  BuildOutlined,
  CheckCircleOutline,
  ThumbUpOutlined,
  ThumbUp,
} from "@mui/icons-material";
import axios from "axios";
import { useWhitelabel } from "../context/WhitelabelContext";
import { getInstanceConfig, getLogoMode, hostname } from "../utils";
import { useNavigate } from "react-router-dom";
import BrandedPageFooter from "../components/BrandedPageFooter";

interface RoadmapProps {
  mode: string;
}

interface RoadmapItem {
  id: string;
  title: string;
  description: string;
  status: string;
  createdAt: string;
  releasedAt: string | null;
  votes: number;
}

interface RoadmapData {
  near_term: RoadmapItem[];
  mid_term: RoadmapItem[];
  long_term: RoadmapItem[];
  released: RoadmapItem[];
}

const Roadmap: React.FC<RoadmapProps> = ({ mode }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { whitelabelSettings, whitelabelLogos } = useWhitelabel();
  const instanceConfig = getInstanceConfig(mode, whitelabelSettings);

  const [roadmapData, setRoadmapData] = useState<RoadmapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [votedItems, setVotedItems] = useState<Set<string>>(() => {
    // Load voted items from localStorage
    const stored = localStorage.getItem("roadmap_voted_items");
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  // Get logo URL with dark mode support
  const getLogoUrl = () => {
    if (mode === "dark" && whitelabelLogos?.horizontal_logo_dark?.url) {
      return whitelabelLogos.horizontal_logo_dark.url;
    }
    if (whitelabelLogos?.horizontal_logo?.url) {
      return whitelabelLogos.horizontal_logo.url;
    }
    return getLogoMode(mode);
  };

  const appName = whitelabelSettings?.title || "GTM AI";

  // Load roadmap data from backend
  useEffect(() => {
    const loadRoadmapData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${hostname}/public/roadmap`);
        setRoadmapData(response.data);
      } catch (error) {
        console.error("Error loading roadmap data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadRoadmapData();
  }, []);

  const handleVote = async (itemId: string) => {
    if (votedItems.has(itemId)) return;

    try {
      const response = await axios.post(
        `${hostname}/public/roadmap/${itemId}/vote`
      );
      const updatedItem = response.data;

      // Update the item in roadmapData
      if (roadmapData) {
        const newData = { ...roadmapData };
        const statusKey = updatedItem.status as keyof RoadmapData;
        if (newData[statusKey]) {
          newData[statusKey] = newData[statusKey].map((item) =>
            item.id === itemId ? { ...item, votes: updatedItem.votes } : item
          );
          setRoadmapData(newData);
        }
      }

      // Mark as voted and save to localStorage
      const newVotedItems = new Set(votedItems);
      newVotedItems.add(itemId);
      setVotedItems(newVotedItems);
      localStorage.setItem(
        "roadmap_voted_items",
        JSON.stringify([...newVotedItems])
      );
    } catch (error) {
      console.error("Error voting for roadmap item:", error);
    }
  };

  const columns = [
    {
      key: "near_term" as const,
      title: "Near Term",
      icon: <RocketLaunchOutlined />,
      color: theme.palette.success.main,
      description: "Coming soon",
    },
    {
      key: "mid_term" as const,
      title: "Mid Term",
      icon: <BuildOutlined />,
      color: theme.palette.warning.main,
      description: "In development",
    },
    {
      key: "long_term" as const,
      title: "Long Term",
      icon: <ScheduleOutlined />,
      color: theme.palette.info.main,
      description: "Planned",
    },
    {
      key: "released" as const,
      title: "Released",
      icon: <CheckCircleOutline />,
      color: instanceConfig.primaryColor,
      description: "Shipped",
    },
  ];

  const RoadmapCard: React.FC<{ item: RoadmapItem }> = ({ item }) => {
    const hasVoted = votedItems.has(item.id);
    const isReleased = item.status === "released";

    return (
      <Card
        sx={{
          mb: 2,
          transition: "transform 0.2s, box-shadow 0.2s",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: theme.shadows[4],
          },
        }}
      >
        <CardContent sx={{ py: 2, px: 2, "&:last-child": { pb: 2 } }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {item.title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {item.description}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 1.5,
            }}
          >
            {item.releasedAt ? (
              <Chip
                label={`Released ${new Date(item.releasedAt).toLocaleDateString()}`}
                size="small"
                color="success"
              />
            ) : (
              <Box />
            )}
            {!isReleased && (
              <Tooltip
                title={hasVoted ? "You've voted for this" : "Vote for this feature"}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <IconButton
                    size="small"
                    onClick={() => handleVote(item.id)}
                    disabled={hasVoted}
                    sx={{
                      color: hasVoted
                        ? instanceConfig.primaryColor
                        : "text.secondary",
                      "&:hover": {
                        color: instanceConfig.primaryColor,
                      },
                    }}
                  >
                    {hasVoted ? (
                      <ThumbUp fontSize="small" />
                    ) : (
                      <ThumbUpOutlined fontSize="small" />
                    )}
                  </IconButton>
                  <Typography
                    variant="body2"
                    color={hasVoted ? "primary" : "text.secondary"}
                    sx={{ ml: 0.5, fontWeight: hasVoted ? 600 : 400 }}
                  >
                    {item.votes || 0}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const ColumnSkeleton = () => (
    <Box>
      {[1, 2, 3].map((i) => (
        <Card key={i} sx={{ mb: 2 }}>
          <CardContent>
            <Skeleton variant="text" width="80%" height={24} />
            <Skeleton variant="text" width="100%" height={16} />
            <Skeleton variant="text" width="60%" height={16} />
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
          py: 6,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Box
            component="img"
            src={getLogoUrl()}
            alt={appName}
            sx={{
              height: 48,
              mb: 3,
              objectFit: "contain",
              cursor: "pointer",
            }}
            onClick={() => navigate("/")}
          />
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ color: instanceConfig.primaryColor }}
          >
            Product Roadmap
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto" }}
          >
            See what we're working on and what's coming next. Feature requests
            from our community help shape our roadmap.
          </Typography>
        </Container>
      </Box>

      {/* Kanban Board */}
      <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 3,
          }}
        >
          {columns.map((column) => (
            <Box key={column.key}>
              {/* Column Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  pb: 2,
                  borderBottom: 2,
                  borderColor: column.color,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: alpha(column.color, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: column.color,
                    mr: 1.5,
                  }}
                >
                  {column.icon}
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {column.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {column.description}
                  </Typography>
                </Box>
                {roadmapData && (
                  <Chip
                    label={roadmapData[column.key].length}
                    size="small"
                    sx={{ ml: "auto" }}
                  />
                )}
              </Box>

              {/* Column Content */}
              <Box
                sx={{
                  minHeight: 200,
                  bgcolor: alpha(theme.palette.background.paper, 0.5),
                  borderRadius: 2,
                  p: 1.5,
                }}
              >
                {loading ? (
                  <ColumnSkeleton />
                ) : roadmapData && roadmapData[column.key].length > 0 ? (
                  roadmapData[column.key].map((item) => (
                    <RoadmapCard key={item.id} item={item} />
                  ))
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 150,
                      color: "text.secondary",
                    }}
                  >
                    <Typography variant="body2">No items</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Container>

      <BrandedPageFooter currentPage="roadmap" />
    </Box>
  );
};

export default Roadmap;
